import { HttpClient } from "@angular/common/http";
import { TranslateService } from "@ngx-translate/core";
import { AppService } from "./../../app.service";
import { UserProfileService } from "./../../user-profile/user-profile.service";
import { LoginService } from "./../../login/login.service";
import {
  Component,
  OnInit,
  Output,
  EventEmitter,
  ViewEncapsulation,
  OnDestroy,
  ViewChild,
  ElementRef,
} from "@angular/core";
import { ActivatedRoute, Router, NavigationEnd } from "@angular/router";
import { MessageService } from "primeng/api";
import { SharedService } from "../shared.service";
import { Subscription } from "rxjs";
import { PermissionService } from "../permissions/permissions.service";
import { LocationItemService } from "../../sales/location-item/location-item.service";
import { AppUtils } from "../../app.utils";

@Component({
  selector: "ba-header",
  templateUrl: "./header.component.html",
  styleUrls: ["./header.component.css"],
  encapsulation: ViewEncapsulation.None,
})
export class HeaderComponent implements OnInit, OnDestroy {
  isSideNavigationOpen: any;
  currentRoute: any;
  userName: string;
  imageUrl: any;
  crumbs: any = [];
  adminRole: boolean;
  permissions: Array<any> = [];
  userData: Object;
  navSubscription: Subscription;
  userNameSubscription: Subscription;
  profileSubscription: Subscription;
  profileImageSubscription: Subscription;
  resetPasswordModal: boolean;
  loyaltySearch: boolean;
  showFilteredResult: boolean;
  @ViewChild("searchInput")
  loyaltyNumSearchInputRef: ElementRef<HTMLInputElement>;
  @Output() updateNavigationState = new EventEmitter();
  constructor(
    public route: ActivatedRoute,
    public router: Router,
    public loginService: LoginService,
    public messageService: MessageService,
    public sharedService: SharedService,
    public appService: AppService,
    public userProfileService: UserProfileService,
    public permissionService: PermissionService,
    public translate: TranslateService,
    public httpClient: HttpClient,
    public locationItemService: LocationItemService,
    public appUtils: AppUtils
  ) {
    this.adminRole = this.permissionService.isAdmin;
    this.permissions = this.permissionService.getCurrentPermissions();

    document.addEventListener("click", this.offClickHandler.bind(this));
    this.appService.visibleLoyaltySearchInputs?.subscribe((res: any) => {
      this.loyaltySearch = res;
    });
  }

  ngOnInit() {
    if (this.route.firstChild && this.route.firstChild.snapshot) {
      const childSnapshot = this.route.firstChild?.snapshot.children[0];
      const { parentRoute, routePath } = childSnapshot.data;
      this.currentRoute = childSnapshot.data.topHeader;
      this.getBreadCrumbData(this.currentRoute, parentRoute, routePath);
    }
    this.router.events.subscribe((routeObj) => {
      if (routeObj instanceof NavigationEnd) {
        if (this.route.firstChild && this.route.firstChild.snapshot) {
          const childSnapshot = this.route.firstChild?.snapshot.children[0];
          const { parentRoute, routePath } = childSnapshot?.data || {
            parentRoute: "",
            routePath: "",
          };
          this.currentRoute = childSnapshot?.data?.topHeader || "";
          this.getBreadCrumbData(this.currentRoute, parentRoute, routePath);
        } else {
          this.currentRoute = "";
          this.getBreadCrumbData(this.currentRoute);
        }
      }
    });
    this.getSubscriptionData();
    this.getLoggedInUserDetail();
    this.getUserProfileData();

    this.appService.getUpdateLoyaltyNumberSearchInputSubject$?.subscribe(
      (keywords: string) => {
        if (keywords) {
          this.setLoyaltyNumberSearchInput(keywords);
          this.showFilteredResult = true;
        }
      }
    );
  }

  ngOnDestroy() {
    this.navSubscription.unsubscribe();
    this.profileImageSubscription.unsubscribe();
    this.profileSubscription.unsubscribe();
  }

  onReloadClick() {
    this.appService.headerReloadClick.next();
  }

  getBreadCrumbData(route: any, parentRoute?: any, routePath?: any) {
    if (parentRoute) {
      this.crumbs = [
        { description: this.translate.instant(parentRoute), url: routePath },
        { description: this.translate.instant(route), url: "" },
      ];
    } else if (route) {
      this.crumbs = [{ description: this.translate.instant(route), url: "" }];
    }
  }

  /**
   * To get subscribed data from components
   */
  getSubscriptionData() {
    this.navSubscription = this.appService.updatedNavigationState$.subscribe(
      (state) => {
        this.isSideNavigationOpen = state;
      }
    );
    this.profileImageSubscription =
      this.appService.updatedUserProfileImage$.subscribe((profileImage) => {
        this.imageUrl = profileImage;
      });
    this.profileSubscription = this.appService.updatedUserData$.subscribe(
      (data) => {
        this.userData = data;
        this.userName = data?.username;
      }
    );
  }

  /**
   * To get logged in user name from local storage
   */
  getLoggedInUserDetail() {
    const user: any = JSON.parse(localStorage.getItem("loggedInUser"));
    this.userName = user && user.username;
  }

  /**
   * To get logged in user profile required for showing profile image in header
   */
  getUserProfileData() {
    if (localStorage.getItem("userProfileData")) {
      const userProfileData = JSON.parse(
        localStorage.getItem("userProfileData")
      );
      this.userData = userProfileData;
      this.appService.updateUserData(this.userData);
      if (
        userProfileData.hasProfileImage ||
        (userProfileData.hasProfileImage &&
          this.permissions.indexOf("getUserImage") > -1)
      ) {
        this.getProfileImage();
      }
    } else {
      this.userProfileService.getUserProfile().subscribe((response) => {
        this.userData = response;
        this.appService.updateUserData(this.userData);
        if (
          response.hasProfileImage ||
          (response.hasProfileImage &&
            this.permissions.indexOf("getUserImage") > -1)
        ) {
          this.getProfileImage();
        }
      });
    }
  }

  /**
   * To get user profile image through blob
   */
  getProfileImage() {
    this.sharedService.getProfileImageFromBlob().subscribe((blobValue) => {
      this.createImageFromBlob(blobValue);
    });
  }

  /**
   * For creating image from blob
   * @param image Image
   */
  createImageFromBlob(image: Blob) {
    const reader = new FileReader();
    reader.addEventListener(
      "load",
      () => {
        this.imageUrl = reader.result;
        this.appService.updateUserProfileImage(this.imageUrl);
      },
      false
    );
    if (image) {
      reader.readAsDataURL(image);
    }
  }

  /**
   * openSidebar will open the sidebar on click of mobile menu icon
   */
  openSidebar(): void {
    this.isSideNavigationOpen = !this.isSideNavigationOpen;
    this.updateNavigationState.emit(this.isSideNavigationOpen);
    this.appService.updateWidthForReporting.next(this.isSideNavigationOpen);
  }

  /**
   * openScanner will open the QR code scanner
   */
  openScanner() {
    this.appService.openLoyaltyNumberScanner.next();
  }

  /**
   * offClickHandler will close the sidebar by clicking anywhere in the page
   * @param event Clicked Target
   */
  offClickHandler(event: any) {
    const getWindowWidth = window.innerWidth;
    if (getWindowWidth <= 840) {
      if (!event.target.closest(".mobile-menu, .navigationBar")) {
        this.isSideNavigationOpen = false;
        this.updateNavigationState.emit(this.isSideNavigationOpen);
      }
    }
  }

  /**
   * logOutUser will log out the loggedin user
   */
  logOutUser() {
    localStorage.setItem("globalPresetFlag", JSON.stringify(false));
    const presetData = localStorage.getItem("globalPresetData")
      ? JSON.parse(localStorage.getItem("globalPresetData"))
      : null;
    if (presetData && (presetData["id"] || presetData["view"])) {
      presetData["columns"] = JSON.stringify(presetData["columns"]);
      this.updateSelectedPreset(presetData);
    }
    this.loginService.logOut(true).subscribe(() => {
      localStorage.clear();
      this.appService.changeSwitchUserState();
      this.appService.updateUserProfileImage("");
      this.router.navigate(["login"]);
    });
  }

  /**
   * updateSelectedPreset will update the preset for all
   */
  updateSelectedPreset(presetDataToSave: any) {
    this.locationItemService
      .updateSelectedPreset(presetDataToSave)
      .subscribe(() => {});
  }

  /**
   * To search loyalty Search
   */
  setLoyaltySearch(data: any) {
    this.appService.loyaltySearchInputs.next(data);
  }

  /**
   * fill loyalty number search input from scanned data
   * @param keywords string
   */
  setLoyaltyNumberSearchInput(keywords: string) {
    if (this.loyaltyNumSearchInputRef) {
      this.loyaltyNumSearchInputRef.nativeElement.value = keywords;
    }
  }

  checkSearchResult() {
    this.showFilteredResult = this.appUtils.checkSearchFilterLength(
      this.loyaltyNumSearchInputRef.nativeElement.value
    );
  }

  clearSearchResult() {
    this.loyaltyNumSearchInputRef.nativeElement.value = "";
    this.showFilteredResult = false;
    this.appService.loyaltySearchInputs.next();
  }
}
